import { TAGESZIEL_MAX, TAGESZIEL_MIN } from '@netcon-work/shared'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { trpc } from '../../lib/trpc'
import { personenQuery } from '../../lib/queries'
import { Button } from '../ui/primitives'

/**
 * **Rhythmus und Kontinuität** – zwei der fünf Mechaniken aus dem Entwurf
 * ([Z-02](../../../../../docs/phasen/zusatz-02-mein-tag.md)).
 *
 * Spaß entsteht hier nicht durch Abzeichen, sondern durch Rhythmus, Fortschritt und sichtbares
 * Fertigwerden. Bewusst weggelassen sind Ranglisten, XP, Konfetti und rote Überfällig-Wände –
 * die Begründungen stehen im Entwurf.
 *
 * ## Das Ziel wird da gesetzt, wo es steht
 *
 * Nils, 24.08.2026 ([F-63](../../../../../docs/OFFENE-FRAGEN.md)): **Schnitt vorschlagen,
 * überschreibbar lassen.** Beides zusammen ist die einzige Variante ohne Nachteil – vom ersten
 * Tag an steht eine Zahl da, und wer sein eigenes Ziel will, bekommt es.
 *
 * Der Schalter dafür sitzt **am Ring** und nicht in den Einstellungen. Ein Feld auf einer Seite,
 * die man einmal im Quartal öffnet, wird nie gefunden; hier steht es an der Zahl, um die es
 * geht. „Aus meinem Schnitt" bleibt als Rückweg – sonst wäre eine einmal gesetzte Zahl für
 * immer gesetzt.
 */
export function Tagesziel({
  fertig,
  ziel,
  serie,
  eigenes,
  ichId,
}: {
  fertig: number
  ziel: number
  serie: number
  /** Was in der Datenbank steht: `null` heißt „aus dem Schnitt". */
  eigenes: number | null
  ichId: string | null
}) {
  const [offen, setOffen] = useState(false)
  const anteil = ziel > 0 ? Math.min(fertig / ziel, 1) : 0
  const umfang = 2 * Math.PI * 13
  const erreicht = fertig >= ziel

  const qc = useQueryClient()
  const setzen = useMutation({
    mutationFn: (wert: number | null) => trpc.people.tageszielSetzen.mutate({ tagesziel: wert }),
    /**
     * Optimistisch wie alles andere (Leitplanke 1): der Ring springt sofort. Er zeigt eine
     * Zahl, die man selbst gerade eingetippt hat – auf eine Serverantwort dafür zu warten
     * wäre der Spinner, den es hier nicht geben darf.
     */
    onMutate: async (wert) => {
      await qc.cancelQueries({ queryKey: personenQuery.queryKey })
      const vorher = qc.getQueryData(personenQuery.queryKey)
      qc.setQueryData(personenQuery.queryKey, (alt) =>
        /* Über die Kennung, nicht über den alten Wert: sonst bekämen alle Kolleginnen und
           Kollegen mit demselben Ziel meine neue Zahl. */
        alt?.map((p) => (p.id === ichId ? { ...p, tagesziel: wert } : p)),
      )
      setOffen(false)
      return vorher
    },
    onError: (_f, _v, vorher) => qc.setQueryData(personenQuery.queryKey, vorher),
    onSettled: () => qc.invalidateQueries({ queryKey: personenQuery.queryKey }),
  })

  return (
    <div className="relative flex items-center gap-3">
      <button
        type="button"
        onClick={() => setOffen((o) => !o)}
        aria-expanded={offen}
        className="flex cursor-pointer items-center gap-[9px] rounded-lg px-1 py-0.5 hover:bg-page"
        title={
          eigenes === null
            ? 'Vorgeschlagen aus deinem Schnitt der letzten vier Wochen. Klicken, um ein eigenes Ziel zu setzen.'
            : `Dein eigenes Ziel: ${eigenes}. Klicken zum Ändern.`
        }
      >
        <svg width="34" height="34" viewBox="0 0 34 34" aria-hidden="true">
          <circle cx="17" cy="17" r="13" fill="none" stroke="var(--color-line)" strokeWidth="4" />
          <circle
            cx="17"
            cy="17"
            r="13"
            fill="none"
            stroke={erreicht ? 'var(--color-good)' : 'var(--color-accent)'}
            strokeWidth="4"
            strokeLinecap="round"
            strokeDasharray={umfang}
            strokeDashoffset={umfang * (1 - anteil)}
            transform="rotate(-90 17 17)"
            /* Der Ring füllt sich sichtbar, wenn ein Häkchen dazukommt – das ist die Belohnung. */
            style={{ transition: 'stroke-dashoffset .5s ease' }}
          />
        </svg>
        <span className="text-left leading-tight">
          <b className="tabular block text-[13px]">
            {fertig} / {ziel}
          </b>
          {/* Unter 1400 px nur die Zahl – das Wort steht im Titel des Knopfs (Z-22). */}
          <span className="block text-[10.5px] text-muted max-[1399px]:hidden">
            {erreicht ? 'Tag geschafft' : 'Tagesziel'}
          </span>
        </span>
      </button>

      {offen && (
        <div className="absolute top-full left-0 z-30 mt-1.5 w-[230px] rounded-card border border-line2 bg-surface p-3 shadow-card">
          <p className="mb-2 text-[12px] text-ink2">
            Wie viele Aufgaben sind für dich ein guter Tag?
          </p>
          <div className="flex flex-wrap gap-1">
            {Array.from(
              { length: TAGESZIEL_MAX - TAGESZIEL_MIN + 1 },
              (_, i) => i + TAGESZIEL_MIN,
            ).map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => setzen.mutate(n)}
                aria-pressed={eigenes === n}
                className={`tabular h-7 w-7 cursor-pointer rounded-lg text-[12.5px] font-semibold ${
                  eigenes === n
                    ? 'bg-ink text-white'
                    : 'border border-line2 text-ink2 hover:bg-page'
                }`}
              >
                {n}
              </button>
            ))}
          </div>
          {/*
            Der Rückweg muss dastehen: ohne ihn wäre eine einmal gesetzte Zahl für immer
            gesetzt, und das Ziel folgte der eigenen Entwicklung nicht mehr.
          */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setzen.mutate(null)}
            aria-pressed={eigenes === null}
            className={`mt-2 w-full justify-center ${eigenes === null ? 'border-ink text-ink' : ''}`}
          >
            Aus meinem Schnitt ({ziel})
          </Button>
        </div>
      )}

      {/**
       * **Die Serie steht nur da, wenn es eine gibt.** Eine „0 Tage in Folge" wäre kein
       * Ansporn, sondern ein Vorwurf – und der erste Arbeitstag nach dem Urlaub fängt ohnehin
       * nicht bei null an, weil Abwesenheiten die Serie pausieren.
       */}
      {serie > 0 && (
        <span
          /* `whitespace-nowrap`: ohne das brach „▲ 4 Tage in Folge" auf dem Telefon in
             vier Zeilen um und zog die ganze Kopfzeile in die Länge (Z-10). */
          className="inline-flex items-center gap-1.5 rounded-full bg-good-bg px-2.5 py-1 text-[11.5px] font-semibold whitespace-nowrap text-good-text"
          title="Tage in Folge, an denen du etwas fertig gemacht hast. Urlaub und Wochenende pausieren die Serie, sie brechen sie nicht."
        >
          ▲ {serie}
          {/* Unter 1400 px „▲ 8" – der Rest steht im Titel (Z-22, aufgeräumte Kopfzeile). */}
          <span className="max-[1399px]:hidden">{serie === 1 ? 'Tag' : 'Tage'} in Folge</span>
        </span>
      )}
    </div>
  )
}
